import { resolveId } from '@medplum/core'
import type { Composition, Reference } from '@medplum/fhirtypes'
import { formatDateTime } from '@seen/core/dates'
import type { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import { ParticipantNote } from '@seen/core/models/participantNote'
import { isOfType } from '@seen/core/utils'
import { Badge } from '@seen/ui'
import { IconFileText } from '@tabler/icons-react'
import type { JSX } from 'react'
import { Avatar } from '#src/components/core/Avatar/Avatar.tsx'
import { HoverCard } from '#src/components/core/hover-card/HoverCard.tsx'
import { HoverCardContent } from '#src/components/core/hover-card/HoverCardContent.tsx'
import { HoverCardTrigger } from '#src/components/core/hover-card/HoverCardTrigger.tsx'
import { HoverPractitioner } from '#src/components/core/hover-card/HoverPractitioner.tsx'
import { Link } from '#src/components/core/Link.tsx'
import { useParticipantNoteTitle } from '#src/hooks/useParticipantNoteTitle.ts'
import { useRead } from '#src/hooks/useRead.ts'

export interface NoteSourceBadgeProps {
  comment: CommentCommunication
  size?: 'default' | 'sm'
}

export function NoteSourceBadge({
  comment,
  size = 'default',
}: NoteSourceBadgeProps): JSX.Element | null {
  const compositionRef = comment.partOf.find((ref) =>
    isOfType(ref, 'Composition')
  ) as Reference<Composition> | undefined

  const { data: note } = useRead(ParticipantNote, compositionRef)
  const title = useParticipantNoteTitle(note)

  if (!note) {
    return null
  }

  const href = `/Patient/${resolveId(note.participant)}/visit-notes/${note.id}`

  return (
    <HoverCard openDelay={300} closeDelay={100}>
      <HoverCardTrigger asChild>
        <Link to={href} className="no-underline">
          {size === 'sm' ? (
            <IconFileText className="size-3 cursor-pointer" />
          ) : (
            <Badge variant="outline" className="max-w-[250px] cursor-pointer">
              <IconFileText className="size-3 shrink-0" />
              <span className="truncate">{title}</span>
            </Badge>
          )}
        </Link>
      </HoverCardTrigger>
      <HoverCardContent className="w-72" side="bottom" align="start">
        <div className="flex flex-col gap-2">
          <Link
            to={href}
            className="line-clamp-2 text-sm font-medium hover:underline"
          >
            {title}
          </Link>
          <div className="flex items-center gap-1.5">
            <Avatar profile={note.owner} size="icon" />
            <HoverPractitioner practitioner={note.owner} className="text-xs" />
            {note.period && (
              <span className="ml-auto text-xs text-muted-foreground">
                {formatDateTime(note.period)}
              </span>
            )}
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  )
}
